import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { api, ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { QrisPayloadInput } from '../components/QrisPayloadInput'
import { VoicePanel } from '../components/VoicePanel'
import { FeedsPanel } from '../components/FeedsPanel'

/**
 * Slug tab SEKALIGUS segmen URL-nya (`/admin/users`, `/admin/payment`, ...).
 * Satu daftar untuk keduanya supaya tidak mungkin ada tab yang tidak punya
 * alamat, atau alamat yang tidak menuju tab mana pun.
 *
 * `metrics` sengaja tidak punya segmen sendiri: tab pertama adalah `/admin`
 * polos, sehingga tautan lama ke `/admin` tetap mendarat di tempat yang sama.
 */
const TABS = ['metrics', 'users', 'payment', 'qris', 'ai', 'feeds'] as const

type Tab = (typeof TABS)[number]

const pathTab = (tab: Tab) => (tab === 'metrics' ? '/admin' : `/admin/${tab}`)

/** Satu provider pada satu produk. `source` menunjukkan kunci mana yang dipakai. */
interface AiProviderRow {
  provider: 'deepseek' | 'gemini' | 'elevenlabs'
  source: 'product' | 'env' | 'none'
  keyLast4: string | null
  status: 'active' | 'invalid' | null
  lastError: string | null
  lastUsedAt: number | null
}

interface AiProductRow {
  code: string
  name: string
  providers: AiProviderRow[]
  usage30d: { calls: number; inputTokens: number; outputTokens: number; failed: number }
}

const AI_PROVIDER_LABEL: Record<string, { name: string; hint: string }> = {
  deepseek: { name: 'DeepSeek', hint: 'platform.deepseek.com → API keys · writes pin copy' },
  gemini: { name: 'Google Gemini', hint: 'aistudio.google.com → Get API key · reads images, and voices or transcribes video when chosen below' },
  elevenlabs: { name: 'ElevenLabs', hint: 'elevenlabs.io → Developers → API keys · video voices when chosen above' },
}

interface AdminUser {
  id: string
  email: string
  name: string | null
  role: string
  status: string
  planCode: string
  periodEnd: number | null
  createdAt: number
}

interface AdminOrder {
  id: string
  orderNo: string
  email: string
  planCode: string
  period: string
  uniqueAmount: number
  status: string
  hasProof: boolean
}

/**
 * Satu baris kunci: tempel key, uji, simpan.
 *
 * Key yang sudah tersimpan tidak pernah ditampilkan kembali (hanya empat
 * karakter terakhirnya), jadi kolomnya selalu kosong saat dibuka — mengisinya
 * berarti mengganti, bukan mengedit.
 */
function AiKeyRow({
  productCode,
  info,
  onSaved,
}: {
  productCode: string
  info: AiProviderRow
  onSaved: () => Promise<void>
}) {
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  const label = AI_PROVIDER_LABEL[info.provider]

  async function simpan() {
    setBusy(true)
    try {
      const res = await api.put<{ keyLast4: string }>(`/admin/ai/${productCode}/${info.provider}`, {
        key: key.trim(),
      })
      toast.success(`${label.name}: key tested and saved (••••${res.keyLast4})`)
      setKey('')
      await onSaved()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save key")
    } finally {
      setBusy(false)
    }
  }

  async function hapus() {
    try {
      await api.delete(`/admin/ai/${productCode}/${info.provider}`)
      toast.success(`${label.name}: key deleted — this product goes back to using the shared key`)
      await onSaved()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete key")
    }
  }

  const lencana =
    info.source === 'product'
      ? info.status === 'invalid'
        ? { teks: 'Key has a problem', kelas: 'bg-destructive/15 text-destructive' }
        : { teks: `••••${info.keyLast4}`, kelas: 'bg-emerald-500/15 text-emerald-400' }
      : info.source === 'env'
        ? { teks: 'Shared key (env)', kelas: 'bg-amber-500/15 text-amber-500' }
        : { teks: 'No key yet', kelas: 'bg-muted text-muted-foreground' }

  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{label.name}</p>
          <p className="text-xs text-muted-foreground">{label.hint}</p>
        </div>
        <span className={cn('shrink-0 rounded px-2 py-0.5 text-xs', lencana.kelas)}>
          {lencana.teks}
        </span>
      </div>

      {info.lastError && <p className="mb-2 text-xs text-destructive">{info.lastError}</p>}

      <div className="flex flex-wrap gap-2">
        <input
          type="password"
          className="h-9 min-w-52 flex-1 rounded-md border border-input bg-background px-3 text-sm"
          placeholder={info.source === 'product' ? 'Paste a new key to replace' : 'Paste API key'}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          autoComplete="off"
        />
        <Button size="sm" onClick={() => void simpan()} disabled={busy || key.trim().length < 20}>
          {busy ? 'Testing…' : 'Test & save'}
        </Button>
        {info.source === 'product' && (
          <Button size="sm" variant="ghost" onClick={() => void hapus()}>
            Delete
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Panel admin platform: metrik lintas produk, akun, verifikasi pembayaran,
 * konfigurasi merchant QRIS, dan kunci AI per produk.
 *
 * Override selector Pinterest TIDAK di sini — itu milik produk dan sudah ada di
 * admin Pinboss. Worker identitas tidak boleh tahu apa-apa tentang DOM
 * Pinterest.
 */
export default function AdminPage() {
  const navigate = useNavigate()
  const { tab: tabParam } = useParams<{ tab?: string }>()
  const tab = TABS.find((t) => t === (tabParam ?? 'metrics')) ?? null

  // Segmen asing (`/admin/xyz`, atau tautan tab yang pernah diganti namanya)
  // dikembalikan ke `/admin` — tanpa ini halamannya tampil dengan semua tab
  // mati dan isi yang kosong, seolah admin sedang rusak. `replace` supaya
  // tombol Back tidak memantul balik ke URL yang salah itu.
  useEffect(() => {
    if (tab === null) navigate('/admin', { replace: true })
  }, [tab, navigate])

  const [metrics, setMetrics] = useState<Record<string, number> | null>(null)
  const [usersList, setUsersList] = useState<AdminUser[]>([])
  const [orders, setOrders] = useState<AdminOrder[]>([])
  const [qris, setQris] = useState<{ configured: boolean; merchantName: string | null; hasWebhookToken: boolean } | null>(null)
  const [staticPayload, setStaticPayload] = useState('')
  const [pairing, setPairing] = useState<string | null>(null)
  const [ai, setAi] = useState<{ models: { vision: string; text: string }; products: AiProductRow[] } | null>(null)
  const pairingCanvas = useRef<HTMLCanvasElement>(null)

  /**
   * Lima permintaan, lima nasib yang terpisah.
   *
   * Dulu ini `Promise.all`, dan konsekuensinya baru terasa saat tabel
   * `ai_product_keys` belum ada di produksi: `/admin/ai` membalas 500, promise
   * gabungannya reject, dan SELURUH halaman admin kosong — metrik, user, dan
   * pembayaran yang sebetulnya sehat ikut hilang, tanpa satu pun petunjuk
   * bagian mana yang rusak. `allSettled` membuat kegagalan berhenti di panelnya
   * sendiri dan menyebutkan namanya.
   */
  const load = useCallback(async () => {
    const [m, u, p, q, a] = await Promise.allSettled([
      api.get<Record<string, number>>('/admin/metrics'),
      api.get<{ users: AdminUser[] }>('/admin/users'),
      api.get<{ orders: AdminOrder[] }>('/admin/payments'),
      api.get<{ configured: boolean; merchantName: string | null; hasWebhookToken: boolean }>('/admin/qris'),
      api.get<{ models: { vision: string; text: string }; products: AiProductRow[] }>('/admin/ai'),
    ])

    const gagal: string[] = []
    const catat = (nama: string, alasan: unknown) =>
      gagal.push(`${nama}: ${alasan instanceof ApiError ? alasan.message : "couldn't load"}`)

    if (m.status === 'fulfilled') setMetrics(m.value)
    else catat('Metrics', m.reason)

    if (u.status === 'fulfilled') setUsersList(u.value.users)
    else catat('Users', u.reason)

    if (p.status === 'fulfilled') setOrders(p.value.orders)
    else catat('Payments', p.reason)

    if (q.status === 'fulfilled') setQris(q.value)
    else catat('QRIS', q.reason)

    if (a.status === 'fulfilled') setAi(a.value)
    else catat('AI', a.reason)

    // Satu toast berisi semuanya, bukan enam toast bertumpuk: kalau yang jatuh
    // adalah sesi yang kedaluwarsa, keenamnya gagal sekaligus.
    if (gagal.length > 0) toast.error(gagal.join('\n'))
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (pairing && pairingCanvas.current) {
      void QRCode.toCanvas(pairingCanvas.current, pairing, { width: 220, margin: 2 })
    }
  }, [pairing])

  async function act(path: string, okMsg: string) {
    try {
      await api.post(path)
      toast.success(okMsg)
      await load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Action failed')
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
      </header>

      <div className="flex flex-wrap gap-1 rounded-md bg-muted p-1 w-fit">
        {(
          [
            ['metrics', 'Metrics'],
            ['users', `Users (${usersList.length})`],
            ['payment', `Payments (${orders.filter((o) => o.status === 'awaiting_verification').length})`],
            ['qris', 'QRIS'],
            ['ai', 'AI'],
            ['feeds', 'Feeds'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => navigate(pathTab(id))}
            className={cn(
              'rounded px-3 py-1.5 text-sm',
              tab === id ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'metrics' && metrics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              ['users', 'Total users'],
              ['paidSubscriptions', 'Paid subscriptions'],
              ['mrrIdr', 'MRR (Rp)'],
              // Metrik posting tinggal di admin produk — datanya memang tidak
              // ada di database identitas.
              ['devicesSeen24h', 'Devices active in 24h'],
              ['paymentsAwaitingVerification', 'Awaiting verification'],
            ] as const
          ).map(([key, label]) => (
            <Card key={key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold tabular-nums">
                  {(metrics[key] ?? 0).toLocaleString('en-GB')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <Card>
          <CardContent className="divide-y p-0">
            {usersList.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {u.email}
                    {u.role === 'admin' && <span className="ml-2 rounded bg-primary/15 px-1.5 text-xs text-primary">admin</span>}
                    {u.status === 'suspended' && (
                      <span className="ml-2 rounded bg-destructive/15 px-1.5 text-xs text-destructive">suspended</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {u.planCode}
                    {u.periodEnd && ` until ${new Date(u.periodEnd).toLocaleDateString('en-GB')}`} · joined{' '}
                    {new Date(u.createdAt).toLocaleDateString('en-GB')}
                  </p>
                </div>
                {/* Satu paket untuk seluruh Orisini (migrasi 0006). */}
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const planCode = e.target.value
                    if (!planCode) return
                    void api
                      .post(`/admin/users/${u.id}/set-plan`, { planCode, days: 30 })
                      .then(() => {
                        toast.success(`Plan changed to ${planCode}`)
                        return load()
                      })
                      .catch((err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Failed'))
                    e.target.value = ''
                  }}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                >
                  <option value="">Change plan…</option>
                  {(['free', 'starter', 'pro'] as const).map((plan) => (
                    <option key={plan} value={plan}>
                      {plan}
                      {plan === 'free' ? '' : ' (30 days)'}
                    </option>
                  ))}
                </select>
                {u.role !== 'admin' &&
                  (u.status === 'suspended' ? (
                    <Button variant="outline" size="sm" onClick={() => void act(`/admin/users/${u.id}/unsuspend`, 'User enabled')}>
                      Enable
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => void act(`/admin/users/${u.id}/suspend`, 'User suspended; all their sessions were revoked')}
                    >
                      Suspend
                    </Button>
                  ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === 'payment' && (
        <Card>
          <CardContent className="divide-y p-0">
            {orders.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
                <span className="w-44 shrink-0 font-mono text-xs">{o.orderNo}</span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{o.email}</span>
                <span className="w-32 shrink-0 tabular-nums">Rp{o.uniqueAmount.toLocaleString('id-ID')}</span>
                <span
                  className={cn(
                    'shrink-0 rounded px-1.5 py-0.5 text-xs',
                    o.status === 'paid'
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : o.status === 'awaiting_verification'
                        ? 'bg-amber-500/15 text-amber-500'
                        : 'bg-muted text-muted-foreground',
                  )}
                >
                  {o.status}
                </span>
                {o.hasProof && (
                  <a
                    href={`/api/admin/payments/${o.id}/proof`}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-xs hover:underline"
                  >
                    Proof
                  </a>
                )}
                {o.status === 'awaiting_verification' && (
                  <>
                    <Button size="sm" onClick={() => void act(`/admin/payments/${o.id}/approve`, 'Payment approved')}>
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => void act(`/admin/payments/${o.id}/reject`, 'Payment rejected')}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === 'qris' && (
        <div className="max-w-xl space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Merchant static QR</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {qris?.configured && (
                <p className="text-sm text-emerald-400">
                  Configured: {qris.merchantName ?? '(no merchant name)'}
                </p>
              )}
              <QrisPayloadInput value={staticPayload} onChange={setStaticPayload} />
              <Button
                onClick={() => {
                  void api
                    .put<{ merchantName?: string }>('/admin/qris', { staticPayload, amountMode: 'add' })
                    .then((r) => {
                      toast.success(`Saved — merchant: ${r.merchantName ?? '?'}`)
                      setStaticPayload('')
                      return load()
                    })
                    .catch((err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Invalid payload'))
                }}
                disabled={!staticPayload.trim()}
              >
                Validate & save
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Android payment-detection app</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                Install the <strong>Alusio QRIS</strong> app on the phone that receives the
                merchant's e-wallet notifications, then scan the pairing QR below. The webhook
                token is shown only once.
              </p>
              <Button variant="secondary" onClick={() => {
                void api
                  .post<{ token: string; pairingPayload: string }>('/admin/qris/regenerate-token')
                  .then((r) => {
                    setPairing(r.pairingPayload)
                    toast.success('New token created — scan the QR below with the Android app')
                  })
                  .catch((err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Failed'))
              }}>
                {qris?.hasWebhookToken ? 'Regenerate pairing token' : 'Create pairing token'}
              </Button>
              {pairing && (
                <div className="w-fit rounded-lg bg-white p-2">
                  <canvas ref={pairingCanvas} />
                </div>
              )}
              <p className="text-xs text-amber-500">
                Automatic detection relies on phone notifications (~95–98% reliable) — the manual
                verification path in the Payments tab is its fallback.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'ai' && ai && (
        <div className="max-w-2xl space-y-4">
          <VoicePanel />
          <p className="text-sm text-muted-foreground">
            Each product uses its own API key, so each product's{' '}
            <code className="text-xs">{ai.models.text}</code> and{' '}
            <code className="text-xs">{ai.models.vision}</code> bills read separately in the
            provider dashboard — and can be matched against the token figures below. A product
            still marked <span className="text-amber-500">Shared key</span> uses the env secret, so
            its usage can't be separated yet.
          </p>

          {ai.products.map((p) => (
            <Card key={p.code}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{p.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {(
                    [
                      ['Calls', p.usage30d.calls.toLocaleString('en-GB')],
                      ['Input tokens', p.usage30d.inputTokens.toLocaleString('en-GB')],
                      ['Output tokens', p.usage30d.outputTokens.toLocaleString('en-GB')],
                      ['Failed', p.usage30d.failed.toLocaleString('en-GB')],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="rounded-md bg-muted/50 p-2">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="text-lg font-semibold tabular-nums">{value}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Figures above cover the last 30 days. One call = one pin description, using both
                  models at once.
                </p>

                {p.providers.map((info) => (
                  <AiKeyRow
                    key={info.provider}
                    productCode={p.code}
                    info={info}
                    onSaved={load}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {tab === 'feeds' && <FeedsPanel />}
    </div>
  )
}
